import React, { useState } from 'react';

interface DuplicateMatch {
  id: string;
  name: string;
  email?: string;
  company?: string;
  similarityScore: number;
  matchedFields: string[];
}

interface DuplicateAlertProps {
  duplicates: DuplicateMatch[];
  currentRecordId: string;
  onMerge: (duplicateId: string) => void;
  onDismiss: () => void;
  onViewAll: () => void;
}

const DuplicateAlert: React.FC<DuplicateAlertProps> = ({
  duplicates,
  currentRecordId,
  onMerge,
  onDismiss,
  onViewAll
}) => {
  const [selectedDuplicate, setSelectedDuplicate] = useState<string | null>(null);
  const [showMergeModal, setShowMergeModal] = useState(false);

  if (duplicates.length === 0) return null;

  const topDuplicate = duplicates[0];
  const hasMultipleDuplicates = duplicates.length > 1;

  return (
    <>
      <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4 shadow-sm">
        <div className="flex items-start gap-3">
          {/* Warning Icon */}
          <div className="flex-shrink-0">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
              <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-amber-900 mb-1">
              {hasMultipleDuplicates 
                ? `${duplicates.length} potential duplicates found`
                : 'Potential duplicate found'
              }
            </h3>
            <p className="text-sm text-amber-700 mb-3">
              This contact appears similar to existing records. Review and merge to maintain data quality.
            </p>

            {/* Top Duplicate Preview */}
            <div className="rounded-lg border border-amber-200 bg-white p-3 mb-3">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{topDuplicate.name}</div>
                  {topDuplicate.email && (
                    <div className="text-sm text-gray-600">{topDuplicate.email}</div>
                  )}
                  {topDuplicate.company && (
                    <div className="text-xs text-gray-500">{topDuplicate.company}</div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                    {Math.round(topDuplicate.similarityScore * 100)}% match
                  </span>
                </div>
              </div>

              {/* Matched Fields */}
              <div className="flex flex-wrap gap-1">
                {topDuplicate.matchedFields.map((field) => (
                  <span key={field} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                    <svg className="w-3 h-3 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {field}
                  </span>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setSelectedDuplicate(topDuplicate.id);
                  setShowMergeModal(true);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                Merge Records
              </button>

              {hasMultipleDuplicates && (
                <button
                  onClick={onViewAll}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-300 bg-white text-amber-700 text-sm font-medium hover:bg-amber-50 transition-colors"
                >
                  View All {duplicates.length}
                </button>
              )}

              <button
                onClick={onDismiss}
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-amber-700 text-sm font-medium hover:bg-amber-100 transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onDismiss}
            className="flex-shrink-0 text-amber-600 hover:text-amber-800 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Merge Confirmation Modal */}
      {showMergeModal && selectedDuplicate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMergeModal(false)}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Merge Duplicate Records?
                </h3>
                <p className="text-sm text-gray-600">
                  This action will combine both records into one. You'll be able to review and choose which data to keep before finalizing.
                </p>
              </div>
            </div>

            <div className="rounded-lg bg-gray-50 p-4 space-y-3">
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Current Record</div>
                <div className="text-sm font-medium text-gray-900">Will be kept as primary</div>
              </div>
              <div className="border-t border-gray-200 pt-3">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Duplicate Record</div>
                <div className="text-sm font-medium text-gray-900">
                  {duplicates.find(d => d.id === selectedDuplicate)?.name}
                </div>
                <div className="text-xs text-gray-500">Will be merged and removed</div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowMergeModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onMerge(selectedDuplicate);
                  setShowMergeModal(false);
                }}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              >
                Continue to Merge
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DuplicateAlert;
